import type { GetStaticProps, InferGetStaticPropsType } from 'next';

import { motion } from 'framer-motion';
import fs from 'fs';
import useSWR, { SWRConfig, preload } from 'swr';

import AsFeaturedOn from '@/components/shared/lp/home/AsFeaturedOn';
import Features from '@/components/shared/lp/home/Features';
import Hero from '@/components/shared/lp/home/Hero';
import KomoDeFi from '@/components/shared/lp/home/KomoDeFi';
import LatestNews from '@/components/shared/lp/home/LatestNews';
import PortfolioManagement from '@/components/shared/lp/home/PortfolioManagement';
import Secure from '@/components/shared/lp/home/Secure';
import Showcase from '@/components/shared/lp/home/Showcase';
import Stats from '@/components/shared/lp/home/Stats';
import Trustless from '@/components/shared/lp/home/Trustless';
import WhyKomodo from '@/components/shared/lp/home/WhyKomodo';
import { JoinCommunity } from '@/components/shared/lp/komodo-wallet/JoinCommunity';

import { apiEndpoints } from '@/data/API_ENDPOINTS';
import blogData from '@/data/blog/en/allPosts.json';
import { fetchHandler } from '@/shared-functions/fetchHandler';
import { calculateTradingPairs } from '@/shared-functions/helpers.js';
import { CoinData } from '@/store/home/types';
import { CoinsType, TradingData } from '@/types';

const CoinDataUrl = 'https://cache.defi-stats.komodo.earth/api/v3/prices/tickers_v2.json';
const fetcher = (url: string) => fetch(url).then((res) => res.json());

preload(CoinDataUrl, fetcher);

export const getStaticProps = (async () => {
  let tradingData: TradingData;
  try {
    tradingData = await fetchHandler(apiEndpoints.stats);
  } catch (error) {
    console.error('Error fetching trading data:', error);
    tradingData = {
      swaps_24hr: 92,
      swaps_7d: 942,
      swaps_14d: 2286,
      swaps_30d: 4971,
      swaps_all_time: 446212,
      current_liquidity: 0,
      volume_24hr: 0,
    };
  }

  const assetsListed: CoinsType[] = await fetchHandler(apiEndpoints.coins);

  const tradeableAssetsList = assetsListed.filter((asset) => asset.wallet_only !== true);

  const tradingPairs = calculateTradingPairs(tradeableAssetsList.length);

  // Fetch coin data for Hero component
  const pricesResponse = await fetch(CoinDataUrl);
  const pricesData = await pricesResponse.json();

  let blogPosts = blogData.slice(0, 3).map((post) => {
    let postData = fs.readFileSync(`./src/data/blog/en/posts/${post.slug}.json`, 'utf-8');
    let {
      feature_image_info,
      permalink,
      iso_published_at,
      title,
      meta_description,
      readable_published_at,
      primary_tag,
      readingTime,
    } = JSON.parse(postData);
    return {
      feature_image_info,
      permalink,
      iso_published_at,
      title,
      meta_description,
      readable_published_at,
      tagName: primary_tag.name,
      readingTime,
    };
  });

  return {
    props: {
      tradingData,
      assetsListed: assetsListed.length,
      tradingPairs,
      blogPosts,
      fallback: { [CoinDataUrl]: pricesData },
    },
  };
}) satisfies GetStaticProps<{
  tradingData: TradingData;
  assetsListed: number;
  tradingPairs: number;
  blogPosts: any;
  fallback: { [key: string]: CoinData };
}>;

const Home = ({
  tradingData,
  assetsListed,
  tradingPairs,
  blogPosts,
  fallback,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { data: coinData } = useSWR<CoinData>(CoinDataUrl, fetcher);
  // Animation variants for gradient ellipses
  const ellipseAnimation = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1.2, ease: 'easeOut' } },
  };
  return (
    <>
      <div className='absolute z-[11] h-full w-full overflow-hidden opacity-70'>
        {/* <div className='index-ellipse-1 absolute z-[-10]'></div>
        <div className='index-ellipse-2 absolute z-[-10]'></div>
        <div className='index-ellipse-3 absolute z-[-10]'></div>
        <div className='index-ellipse-4 absolute z-[-10]'></div> */}
      </div>
      <main className='container relative z-[100] mx-auto font-sans text-white md:grid md:grid-cols-24 md:gap-0'>
        {/* <div className='lp-ellipse-223 absolute left-[30%] top-[-200px] z-[-10] h-[359px] w-[287px] -translate-x-1/2 lg:left-[40%] -sm:top-[-200px]'></div> */}
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-224 absolute left-[-450px] top-[163px] z-[-10] h-[515px] w-[492px] xl:left-[-400px] -sm:left-[-400px] -sm:top-[400px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-232 absolute right-[-500px] top-[800px] z-[-10] h-[733px] w-[700px] -sm:right-[-300px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-233 absolute left-[-600px] top-[1700px] z-[-10] h-[783px] w-[749px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-234 absolute right-[-700px] top-[2700px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-236 absolute right-[-600px] top-[4500px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-238 absolute right-[-600px] top-[5600px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-239 absolute left-[-600px] top-[6600px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-239 absolute right-[-600px] top-[7800px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-240 absolute bottom-[-500px] left-[-300px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-241 absolute right-[-600px] top-[9000px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <div className='hidden xl:col-span-2 xl:block'></div>
        <div className='relative w-full max-w-7xl px-4 xs:px-8 md:col-span-24 md:mx-auto xl:col-span-20 xl:px-0'>
          <SWRConfig value={{ fallback }}>
            <Hero data={coinData} />
          </SWRConfig>

          <Stats
            tradingData={tradingData}
            assetsListed={assetsListed}
            tradingPairs={tradingPairs}
          />
          <AsFeaturedOn />

          {/* <Airdrop /> */}
          {/* <CoinLogoScroller /> */}
          {/* <CoinLogoScrollerCopy /> */}
          <Secure />
          <Trustless />
          <PortfolioManagement />

          {/* <SpendYourCrypto /> */}
          <WhyKomodo />
          <Features />
          <KomoDeFi />
          <Showcase />

          <JoinCommunity />
          <LatestNews blogPosts={blogPosts} />
        </div>
        <div className='hidden xl:col-span-2 xl:block'></div>
      </main>
    </>
  );
};

export default Home;
